import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { filter, fromEvent } from 'rxjs';

import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Tag,
  VFlex,
  VIconButton,
  VInput,
  VStack,
  new__cn as cn,
  useSubscriptions,
} from '@/package';
import { useLabelingBoxStore, useToolMenuStore } from '@/store';
import type { BoxWithLabel } from '@/type';

import { useToolPlayControllerContext } from '../ToolPlayController';

type ToolSearchTrackIDPopupContentProps = {
  defaultKeyword?: string;
};

const ToolSearchTrackIDPopupContent = ({
  defaultKeyword,
}: ToolSearchTrackIDPopupContentProps) => {
  const { t } = useTranslation();
  const { $toolPlayController } = useToolPlayControllerContext();
  const inputRef = useRef<HTMLInputElement>(null);

  const [activeIndex, setActiveIndex] = useState(-1);
  const [keyword, setKeyword] = useState(defaultKeyword ?? '');

  const boxList = useLabelingBoxStore(state => state.getCurrentBoxList());

  const filteredBoxList = useMemo(() => {
    if (!keyword.trim()) return [] as BoxWithLabel[];

    return boxList.filter(box =>
      box.trackId?.toLowerCase().includes(keyword.trim().toLowerCase()),
    );
  }, [boxList, keyword]);

  const resultCount = filteredBoxList.length;
  const isDownDisabled = activeIndex >= resultCount - 1;
  const isUpDisabled = activeIndex <= 0;

  const selectBox = useCallback(
    (
      ...args: Parameters<
        ReturnType<(typeof useLabelingBoxStore)['getState']>['selectBox']
      >
    ) => {
      useLabelingBoxStore.getState().selectBox(...args);
    },
    [],
  );

  const handleSearch = useCallback(() => {
    if (!keyword.trim() || !filteredBoxList.length) return;

    setActiveIndex(0);
    selectBox(filteredBoxList[0].id, true);
  }, [keyword, filteredBoxList]);

  const navigateResult = useCallback(
    (direction: 'up' | 'down') => {
      if (!filteredBoxList.length) return;

      const nextIndex = direction === 'up' ? activeIndex - 1 : activeIndex + 1;

      if (nextIndex < 0 || nextIndex >= filteredBoxList.length) return;

      setActiveIndex(nextIndex);
      selectBox(filteredBoxList[nextIndex].id);
    },
    [activeIndex, filteredBoxList, selectBox],
  );

  const onItemClick = useCallback((box: BoxWithLabel, index: number) => {
    setActiveIndex(index);
    selectBox(box.id);
  }, []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleSearch();
        return;
      }

      if (!filteredBoxList.length) return;

      if (event.key === 'ArrowDown' && activeIndex === -1) {
        setActiveIndex(0);
        selectBox(filteredBoxList[0].id);
        return;
      }

      if (event.key === 'ArrowUp' && activeIndex === -1) {
        const lastIndex = filteredBoxList.length - 1;

        setActiveIndex(lastIndex);
        selectBox(filteredBoxList[lastIndex].id);
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      event.preventDefault();
      navigateResult(event.key === 'ArrowUp' ? 'up' : 'down');
    },
    [activeIndex, filteredBoxList, handleSearch, navigateResult, selectBox],
  );

  const onKeyDownRef = useRef(onKeyDown);

  useEffect(() => {
    onKeyDownRef.current = onKeyDown;
  }, [onKeyDown]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setKeyword(defaultKeyword ?? '');
  }, [defaultKeyword]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useSubscriptions(() => {
    if (!inputRef.current) return [];

    return [
      fromEvent<KeyboardEvent>(inputRef.current, 'keydown')
        .pipe(
          filter(event =>
            ['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key),
          ),
        )
        .subscribe(event => onKeyDownRef.current(event)),
    ];
  }, []);

  return (
    <VStack
      className="absolute top-0 left-0 w-[314px] overflow-hidden border-r border-r-gray-200 bg-white p-2"
      style={{
        bottom: `${$toolPlayController.current?.offsetHeight ?? 0}px`,
      }}
      gap={1}
    >
      <VFlex className="w-full" items="stretch">
        <div className="flex-1 text-body-lg font-bold text-blue-300">
          {t('labeling.searchTrackIDPopupTitle.dilutor')}
        </div>
      </VFlex>

      <VStack className="w-full border-b border-b-gray-200 pb-2" gap={0.5}>
        <VFlex items="center" justify="between">
          <VInput
            fluid
            placeholder="Track ID"
            ref={inputRef}
            size="sm"
            type="search"
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
          />
        </VFlex>
      </VStack>

      {keyword.trim() && (
        <VFlex className="w-full" items="center" gap={0.25}>
          <div className="flex-1 text-xs leading-[18px] text-gray-900">
            {t('labeling.searchTrackIDPopupSearchCount.injure', {
              count: resultCount,
            })}
          </div>

          <VFlex gap={0.25}>
            <VIconButton
              Icon={KeyboardArrowUp}
              disabled={isUpDisabled}
              size="sm"
              variant="roundPrimary"
              onClick={() => !isUpDisabled && navigateResult('up')}
            />
            <VIconButton
              Icon={KeyboardArrowDown}
              disabled={isDownDisabled}
              size="sm"
              variant="roundPrimary"
              onClick={() => !isDownDisabled && navigateResult('down')}
            />
          </VFlex>
        </VFlex>
      )}

      {keyword.trim() && (
        <VStack className="flex-1 overflow-y-auto border-t border-t-gray-200">
          {filteredBoxList.map((box, index) => (
            <VFlex
              key={box.id}
              className={cn(
                'w-full cursor-pointer px-2 py-0.5',
                index === activeIndex ? 'bg-[#DFF2FB]' : 'hover:bg-gray-100',
                index > 0 && 'border-t border-t-gray-200',
              )}
              items="center"
              gap={0.25}
              role="button"
              onClick={() => onItemClick(box, index)}
            >
              <VFlex items="center" gap={0.25} className="min-w-0 flex-1">
                <Tag className="size-4 shrink-0 text-secondary-500" />
                <span className="truncate text-xs leading-[18px] text-gray-900">
                  {box.trackId}
                </span>
              </VFlex>
            </VFlex>
          ))}
        </VStack>
      )}
    </VStack>
  );
};

export const ToolSearchTrackIDPopup = () => {
  const toolMap = useToolMenuStore(state => state.toolMap);
  const [keyword, setKeyword] = useState('');
  const visible = useMemo(
    () => useToolMenuStore.getState().isSelectedByToolName('searchTrackID'),
    [toolMap],
  );
  const getSelectedBox = () => {
    return useLabelingBoxStore.getState().getCurrentSelectedBoxList()?.[0];
  };

  useEffect(() => {
    if (!visible) return;

    const selectedBox = getSelectedBox();

    if (selectedBox) {
      setKeyword(selectedBox.trackId ?? '');
    }
  }, [visible]);

  useEffect(() => {
    const searchTrackIDTool = useToolMenuStore
      .getState()
      .findByName('searchTrackID');

    if (!searchTrackIDTool || !searchTrackIDTool.command) return;

    if (searchTrackIDTool.isSelected) {
      if (visible) {
        const selectedBox = getSelectedBox();

        if (selectedBox) {
          setKeyword(selectedBox.trackId ?? '');
        }
      }
    }
  }, [toolMap]);

  if (!visible) return null;

  return <ToolSearchTrackIDPopupContent defaultKeyword={keyword} />;
};

ToolSearchTrackIDPopup.displayName = 'ToolSearchTrackIDPopup';
ToolSearchTrackIDPopupContent.displayName = 'ToolSearchTrackIDPopupContent';
